// BO NIN
import { Component, Input, OnInit } from '@angular/core';
import { OnboardingV2Service } from '@mpt-fo/common/core/services/onboarding-v2.service';
import { CustomTimelineVersion } from '@mpt-fo/common/kamona/model/kamonal-model';
import { ModalService } from '@mpt-fo/common/lean-shared/components/nz-modal-service/modal.service';
import {
  BvnIdentityForm,
  ConfirmNinID,
  DefaultIdentityForm,
  ErrorCode,
  KYC_IDENTITY_TYPE,
  KycDictionary,
  KYCFormKeys,
  KycIdentityToken,
  KycIdentityType,
  KycIdentityTypeStatus,
  KYCLevel,
  KYCLvl1StepV4,
  KycRoutes,
  NinIdentityForm,
} from '@app/channels/onboarding/onboarding-v2/model/onboarding-v2-model';
import { TimelineModel } from '@mpt-fo/common/shared/models/timeline.model';
import { NzModalRef } from 'ng-zorro-antd/modal';
import {
  BoKycLvl1BvnVerificationComponent,
} from '@app/channels/onboarding/onboarding-v2/kyc-v2/partials/bo-kyc/partials/bo-kyc-lvl1/partials/bo-kyc-lvl1-bvn-verification/bo-kyc-lvl1-bvn-verification.component';
import { NibssVerificationService } from '@mpt-fo/common/core/services/nibss-verification.service';
import { DataLayerService } from '@mpt-fo/common/core/services/data-layer-service.service';
import { LocalStorageService } from '@mpt-fo/common/core/services/local-storage.service';
import {
  MergeDuplicateProfileAccountErrorCode,
} from '@app/channels/settings/merge-duplicate-nin-accounts/models/merge-account';

@Component({
	selector: 'ng-mf-bo-kyc-lvl1-select-identity-type',
	templateUrl: './bo-kyc-lvl1-select-identity-type.component.html',
	styleUrls: ['./bo-kyc-lvl1-select-identity-type.component.scss'],
})
export class BoKycLvl1SelectIdentityTypeComponent extends BoKycLvl1BvnVerificationComponent implements OnInit {
	loading = true;
	submitting: boolean;
	kycLvl1Steps;
	currentFormStep: TimelineModel;
	totalStep: number;
	previousLabel = KycDictionary.Level_1_Upgrade;
	Dictionary = KycDictionary;
	formSteps: TimelineModel[] = [];
	CustomTimelineVersion = CustomTimelineVersion;
	allowNibssBvnVerification: boolean;
	runningModal: NzModalRef;
	useSubmitOnly = false;
	KYC_IDENTITY_TYPE = KYC_IDENTITY_TYPE;
	@Input() currentView: KYC_IDENTITY_TYPE;
	@Input() isEditMode: boolean;
  readonly Confirm_NIN = 'Confirm NIN';
  readonly BVN_OR_NIN = 'BVN or NIN';
  readonly BVN = 'BVN';
  readonly NIN = 'NIN';
	title = 'Select an option';
	subTitle = 'Select the type of ID to validate';
	selectedIdentityType: KYC_IDENTITY_TYPE;
	identityToken: KycIdentityType;

	constructor(
    public onboardingService: OnboardingV2Service,
    public modalService: ModalService,
    nibssService: NibssVerificationService,
		public dataLayerHelper: DataLayerService,
		public localStorage: LocalStorageService) {
		super(onboardingService, modalService, nibssService, dataLayerHelper, localStorage);
	}

	get canSkipIdentitySelection(){
		return this.bvnNotAllowed() || this.isLevelZeroOrAlreadyHasBvn()
	}
 
	get useEnhancedFlow(){
		return this.onboardingService.useEnhancedKycLvl1Flow()
	}

	get showBackButton(){
		return this.useEnhancedFlow && !this.onboardingService.useKycBvnNationality()
	}
	
	get isBvnNinSubmitOnly() {
		if(this.useEnhancedFlow){
			return false;
		}
		return this.canSkipIdentitySelection
	}

	get showTimelineBackButton(){
		return this.onboardingService.showBoKycLvl1BackButton()
	}


	async initialize() {
    await this.initializeParameters();
	}

  async initializeParameters(){
    this.initializeForm();
    if(!this.onboardingService.isUserFromKycHarmonization())
      await this.updateIdentityTypeTimelineIfDetailsAlreadyExist();
  }

	initializeForm(){
		this.allowNibssBvnVerification = this.onboardingService.useNibssBvnVerification();
		this.setBvnIfAlreadySubmitted(this.formData);
    this.setSubmitOnly(false);
		this.setFormParameters();

    if(this.isLevelZeroOrAlreadyHasBvn())
		return this.setFormForUsersWithBvn();

    if(this.bvnNotAllowed())
      return this.handleSkippedIdentitySelectionIfBvnIsNotAllowed();
	}


	async updateIdentityTypeTimelineIfDetailsAlreadyExist(){
		if(!this.canUseIdentityToken()) { return;}
			const identityDetails = await this.onboardingService.getKycIdentityType();
			this.handleIdentityTokenResponse(identityDetails);
	}

	handleIdentityTokenResponse(kycIdentityToken: KycIdentityToken){
    const identityToken = kycIdentityToken?.tokens.find(token => token.type === KYC_IDENTITY_TYPE.NIN);
		const hasToken = this.hasToken(identityToken?.status);

		if(!hasToken){return;}

    this.identityToken = identityToken;
    this.setConfirmNinForm();
	}

  setConfirmNinForm(){
    this.setFormParameter(KYC_IDENTITY_TYPE.CONFIRM_ID);
    this.currentView = KYC_IDENTITY_TYPE.CONFIRM_ID;
  }

  hasToken(status: string){
		return status === KycIdentityTypeStatus.FOUND;
	}

	canUseIdentityToken(){
		return this.onboardingService.canUseIdentityToken() && this.hasNotSubmittedBvnOrNin();
	}

  hasNotSubmittedBvnOrNin(){
    return !this.onboardingService.hasSubmittedBvnOrNin() || this.hasBvnOrNinTimeline();
  }

	hasBvnOrNinTimeline(){
		const timeline = this.formSteps.find(t => t.key === KYCFormKeys.BVN_NIN_VERIFIED);
		return !!timeline;
	}

	updateTimelineDescription(description: string){
		const timeline = this.formSteps.find(t => t.key === KYCFormKeys.BVN_NIN_VERIFIED);
		if(!timeline){ return;}
		timeline.description = description;
	}

	setFormForUsersWithBvn() {
		this.handleSkippedIdentitySelectionForUsersWithBvn();
	}

  isLevelZeroOrAlreadyHasBvn(){
    const kycLevelZero = this.onboardingService.isKycLevelZero();
    const oldSignupUser = this.onboardingService.handleOldSignupUser();
    return kycLevelZero || oldSignupUser;
  }

	setSubmitOnly(useSubmitOnly: boolean) {
		this.useSubmitOnly = useSubmitOnly;
	}

	handleSkippedIdentitySelectionForUsersWithBvn() {
		this.setSubmitOnly(true);
		this.setFormParameter(KYC_IDENTITY_TYPE.BVN);
    this.currentView = KYC_IDENTITY_TYPE.BVN;
	}

  exitConfirmIdForm(){
    if(this.bvnNotAllowed()){
      return this.handleSkippedIdentitySelectionIfBvnIsNotAllowed();
    }
    this.resetForm(undefined);
  }

  bvnNotAllowed(){
    return !this.onboardingService.allowBvnForKycLevel1BusinessOnboarding();
  }

  handleSkippedIdentitySelectionIfBvnIsNotAllowed() {
    this.setSubmitOnly(true);
    this.setFormParameter(KYC_IDENTITY_TYPE.NIN);
    this.currentView = KYC_IDENTITY_TYPE.NIN;
  }

	setFormParameters() {
		const formParameters = this.onboardingService.getBoKycLvl1FormParameters(KYCFormKeys.BVN_NIN_VERIFIED);
		this.totalStep = formParameters?.totalStep;
		this.kycLvl1Steps = formParameters?.kycLvl1Steps;
		this.currentFormStep = formParameters?.currentFormStep;
		this.formSteps = formParameters?.formSteps;
	}

	setBvnIfAlreadySubmitted(formData: { bvn: string; dateOfBirth: string }) {
		if(this.onboardingService.getBankVerificationNumberFromKyc())
    formData.bvn = this.onboardingService.getBankVerificationNumberFromKyc();
	}

	ngOnInit(): void {
    console.log(' ngOnInit BoKycLvl1SelectIdentityTypeComponent ---> ')
  }

	submitForm(idType: KYC_IDENTITY_TYPE) {
		this.currentView = idType;
		this.setFormParameter(idType);
	}

	submitBvn($event: any, existingCustomer = false) {
		this.submit($event, existingCustomer);
	}

	resetForm(idType?: KYC_IDENTITY_TYPE) {
		if(idType && !this.isBvnNinSubmitOnly && this.canSkipIdentitySelection){
			this.goToPreviousStep()
			return
		}
		this.selectedIdentityType = idType;
		this.setFormParameter(undefined);
    	this.currentView = undefined;
	}

	setFormParameter(idType: KYC_IDENTITY_TYPE) {
		switch (idType) {
			case KYC_IDENTITY_TYPE.BVN:
				this.setBvnParameter();
				break;
			case KYC_IDENTITY_TYPE.NIN:
				this.setNinParameter();
				break;
			case KYC_IDENTITY_TYPE.CONFIRM_ID:
				this.setConfirmIDParameter();
				break;
			default:
				this.setDefaultFormParameter();
		}
	}

	setDefaultFormParameter() {
		this.title = DefaultIdentityForm.title;
		this.subTitle = DefaultIdentityForm.subTitle;
    this.updateTimelineDescription(this.BVN_OR_NIN);
	}

	setNinParameter() {
		this.title = NinIdentityForm.title;
		this.subTitle = NinIdentityForm.subTitle;
    this.updateTimelineDescription(this.NIN);
		this.formData.bvn = this.onboardingService.getNationalIdentificationNumberFromKyc();
	}

	setConfirmIDParameter() {
		this.title = ConfirmNinID.title;
		this.subTitle = ConfirmNinID.subTitle;
    this.updateTimelineDescription(this.Confirm_NIN);
	}

	setBvnParameter() {
		this.title = BvnIdentityForm.title;
		this.subTitle = BvnIdentityForm.subTitle;
    this.updateTimelineDescription(this.BVN);
		this.formData.bvn = this.onboardingService.getBankVerificationNumberFromKyc();
	}

	submitNin($event: any, existingCustomer = false, retryBvnNinMatch = false) {
		this.submitting = true;
		const payload = this.buildNinParam($event);
		this.onboardingService.handleValidateNIN({ nin: payload.nin, retryBvnNinMatch }, existingCustomer)
			.then(() => {
				this.handleNinValidationResponse(payload.nin);
			})
			.catch((e) => {
				this.handleValidateNinError(e, payload.nin);
			});
	}

	handleValidateNinError(e, nin) {
		if (e?.error?.responseCode === MergeDuplicateProfileAccountErrorCode.NIN && this.onboardingService.canUseDuplicateProfileMergeFlow()) {
			this.onboardingService.routeToDuplicateMergeFlow(e?.error, 'uprageKYCLevel2');
		} else if (e?.error?.code === ErrorCode.VALIDATE_BVN_NIN_MISMATCH) {
			this.launchBvnNinMismatchModal({ apiData: e.error, nin });
		} else {
			this.handleError(e);
		}
	}

	goToNextOfKinStepForNin(nationalIdentificationNumber: string) {
        const kycData = this.onboardingService.getKycDataV2();
		kycData.hasSubmittedNin = true;
		kycData.livenessVerificationIdentitySource = KYC_IDENTITY_TYPE.NIN;
		kycData.nationalIdentificationNumber = nationalIdentificationNumber;
		this.onboardingService.setKycDataV2(kycData);
		this.routeToNextStep();
		this.submitting = false;
    }

	handleNinValidationResponse(nationalIdentificationNumber) {
		const shouldUseUpgradedLvl2Flow = this.onboardingService.shouldUseUpgradeKycLevel2Flow();
		if (!shouldUseUpgradedLvl2Flow) {
			this.goToNextOfKinStepForNin(nationalIdentificationNumber);
		} else {
			this.onboardingService.routeToVerifyIdentity(KycRoutes.BO_LVL_2_ROUTE, KycRoutes.NIN_BVN_VERIFY_DETAILS, nationalIdentificationNumber);
			this.onboardingService.getResumeNinBvnUpgradeFlow().subscribe((shouldContinue) => {
				if (shouldContinue) {
					this.goToNextOfKinStepForNin(nationalIdentificationNumber);
					this.onboardingService.setResumeNinBvnUpgradeFlow('');
				}
			});
		}
	}

	buildNinParam(obj) {
		return {
			nin: obj?.value?.bvn,
		};
	}

	goToPreviousStep(){
		return this.onboardingService.navigateKycLvl1Form(KYCLvl1StepV4.NATIONALITY);
	}

	goBack() {
		this.onboardingService.routeToKycPrimer(KYCLevel.LVL_1);
	}

	openNinPage() {
		this.onboardingService.openNinPage();
	}
}
